import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { FormError } from '@/components/form/FormError';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { useSubmit } from '@/hooks/useSubmit';
import CurrentUserModel from '@/models/services/CurrentUserModel';
import { newPasswordSchema, PASSWORD_MIN_LENGTH } from '@/security/password';
import { useAuthStore } from '@/stores/useAuthStore';

const schema = newPasswordSchema.and(z.object({ currentPassword: z.string().optional() }));

type PasswordFormValues = z.infer<typeof schema>;

interface PasswordFormProps {
  /** Première définition (compte sans mot de passe local) : pas de mot de passe actuel */
  definition: boolean;
  /** Après l'enregistrement */
  onSaved?: () => void;
  className?: string;
}

/**
 * Mot de passe local de l'utilisateur connecté (PATCH /users/me) : modification (Mon mot de passe) ou première
 * définition (DefinePasswordPage, imposée par RequireAuth)
 */
export function PasswordForm({ definition, onSaved, className }: PasswordFormProps) {
  const markPasswordDefined = useAuthStore((state) => state.markPasswordDefined);
  const { submit, submitting, error } = useSubmit();

  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState: { errors },
  } = useForm<PasswordFormValues>({ resolver: zodResolver(schema) });

  const onSubmit = ({ currentPassword, plainPassword }: PasswordFormValues) => {
    if (!definition && !currentPassword) {
      setFieldError('currentPassword', { message: 'Mot de passe actuel requis' });
      return;
    }

    return submit(async () => {
      await new CurrentUserModel().save(definition ? { plainPassword } : { currentPassword, plainPassword }, {
        silentError: true,
      });
      reset();
      markPasswordDefined();
      toast.success(definition ? 'Votre mot de passe a été défini.' : 'Votre mot de passe a été modifié.');
      onSaved?.();
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className={`space-y-4 ${className ?? ''}`}>
      {!definition && (
        <TextField
          id="currentPassword"
          label="Mot de passe actuel"
          required
          type="password"
          autoComplete="current-password"
          icon={Lock}
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
      )}
      <TextField
        id="plainPassword"
        label="Nouveau mot de passe"
        required
        type="password"
        autoComplete="new-password"
        placeholder={`${PASSWORD_MIN_LENGTH} caractères minimum`}
        icon={Lock}
        error={errors.plainPassword?.message}
        {...register('plainPassword')}
      />
      <TextField
        id="confirmation"
        label="Confirmation"
        required
        type="password"
        autoComplete="new-password"
        icon={Lock}
        error={errors.confirmation?.message}
        {...register('confirmation')}
      />

      <FormError error={error} />

      <Button type="submit" className={definition ? 'w-full' : undefined} disabled={submitting}>
        <KeyRound className="w-4 h-4" />
        {definition ? 'Définir mon mot de passe' : 'Modifier mon mot de passe'}
      </Button>
    </form>
  );
}
